import { Koi } from '@ksyos/koi';
import type { TypedReadSchema } from '@common/formEngine/typedReadSchema.types';
import type { ProcessActivityAsset } from '@common/validation/processActivityAsset';
import type { TypedSchema } from '@common/validation/utils/typedSchemaType';
import { orderedPractitionerTypes, TypeBehandelaar, Vervolgstap } from '@common/customControls/bariatrics';
import { getProcessActivityAssetsSchema } from '@common/validation/processActivityAsset';
import { addAllowNull } from '@common/validation/utils/addAllowNull';
import { getTextAreaStringSchema } from '@common/validation/utils/getTextAreaStringSchema';

export const bariPostPsychologistReportVervolgstappen = [
    Vervolgstap.vervolgafspraak_zelf,
    Vervolgstap.verwijs_lifestylecoach_of_ziekenhuis,
    Vervolgstap.doorverwijzen_extern_buiten_zorgpad,
    Vervolgstap.geen_actie_nodig,
] as const;

export type BariPostPsychologistReportVervolgstap = Extract<
    Vervolgstap,
    (typeof bariPostPsychologistReportVervolgstappen)[number]
>;

export type BariPostPsychologistReportData = {
    assets: ProcessActivityAsset[];
    hulpvraag: string | null;
    interventie: string | null;
    reden_doorverwijzing: string | null;
    type_behandelaar: TypeBehandelaar | null;
    type_behandelaar_overig: string | null;
    vermoedelijke_diagnose: string | null;
    verslag: string | null;
    vervolgstap: BariPostPsychologistReportVervolgstap | null;
};

const bariPostPsychologistReportFinal: TypedSchema<BariPostPsychologistReportData> = {
    assets: getProcessActivityAssetsSchema(),
    hulpvraag: getTextAreaStringSchema(true),
    interventie: getTextAreaStringSchema(true),
    reden_doorverwijzing: Koi.when('vervolgstap', {
        is: Vervolgstap.doorverwijzen_extern_buiten_zorgpad,
        otherwise: Koi.valid(null),
        then: getTextAreaStringSchema(),
    }),
    type_behandelaar: Koi.when('vervolgstap', {
        is: Vervolgstap.doorverwijzen_extern_buiten_zorgpad,
        otherwise: Koi.valid(null),
        then: Koi.valid(...orderedPractitionerTypes),
    }),
    type_behandelaar_overig: Koi.when('type_behandelaar', {
        is: TypeBehandelaar.overig,
        otherwise: Koi.valid(null),
        then: getTextAreaStringSchema(),
    }),
    vermoedelijke_diagnose: Koi.when('vervolgstap', {
        is: Vervolgstap.doorverwijzen_extern_buiten_zorgpad,
        otherwise: Koi.valid(null),
        then: getTextAreaStringSchema(),
    }),
    verslag: getTextAreaStringSchema(),
    vervolgstap: Koi.koi().valid(...bariPostPsychologistReportVervolgstappen),
};

const bariPostPsychologistReportDraft = addAllowNull(bariPostPsychologistReportFinal, [
    'assets',
    'hulpvraag',
    'interventie',
]);

export const bariPostPsychologistReportDraftSchema = Koi.object(bariPostPsychologistReportDraft);
export const bariPostPsychologistReportFinalSchema = Koi.object(bariPostPsychologistReportFinal);

export type BariPostPsychologistReportReadKey = keyof BariPostPsychologistReportData;

export type BariPostPsychologistReportReadLabel =
    | 'Bestand(en)'
    | 'Hulpvraag'
    | 'Interventie'
    | 'Reden doorverwijzing'
    | 'Type behandelaar'
    | 'Vermoedelijke diagnose'
    | 'Verslag'
    | 'Vervolgstap';

export type BariPostPsychologistReportReadSectionTitle = 'Verslag' | 'Vervolgstap';

export const bariPostPsychologistReportReadSchema: TypedReadSchema<
    BariPostPsychologistReportReadKey,
    BariPostPsychologistReportReadLabel,
    BariPostPsychologistReportReadSectionTitle
>[] = [
    {
        items: [
            {
                key: 'verslag',
                label: 'Verslag',
            },
            {
                key: 'hulpvraag',
                label: 'Hulpvraag',
            },
            {
                key: 'interventie',
                label: 'Interventie',
            },
            {
                key: 'assets',
                label: 'Bestand(en)',
            },
        ],
        sectionTitle: 'Verslag',
    },
    {
        items: [
            {
                key: 'vervolgstap',
                label: 'Vervolgstap',
            },
            {
                key: 'vermoedelijke_diagnose',
                label: 'Vermoedelijke diagnose',
            },
            {
                key: 'type_behandelaar',
                label: 'Type behandelaar',
            },
            {
                key: 'reden_doorverwijzing',
                label: 'Reden doorverwijzing',
            },
        ],
        sectionTitle: 'Vervolgstap',
    },
];
